import {
  Component,
  ContentChild,
  DestroyRef,
  EventEmitter,
  Inject,
  Input,
  OnDestroy,
  OnInit,
  Output,
  TemplateRef,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { DynamicDialogRef, DialogService } from 'primeng/dynamicdialog';
import {
  Oversikt,
  OkantNamnStatus,
  AtgardStatus,
  Anitbiotikadubblett,
} from '../../../models/manuell-atgard';
import { NameType, NameStatus } from '../../../models/models';
import {
  Fynd,
  Antibiotika,
  AnalysResultatDTO,
} from '../../../../pages/models/analys-resultat';
import {
  AtgardaEditDialogComponent,
  AtgardResult,
} from '../../../../pages/atgards-lista/atgarda-edit-dialog/atgarda-edit-dialog.component';
import { AtgardsListaService } from '../../../../pages/atgards-lista/atgards-lista.service';
import { HistorikDialogComponent } from '../../../../pages/atgards-lista/historik-dialog/historik-dialog.component';
import { ButtonSetItem } from '../../app-button-set/app-button-set-models';
import { Subject, takeUntil } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { StandardNamnService } from 'src/app/pages/standard-namn/standard-namn.service';

@Component({
    selector: 'app-atgards-details',
    templateUrl: './atgards-details.component.html',
    styleUrls: ['./atgards-details.component.scss'],
    standalone: false
})
export class AtgardsDetailsComponent implements OnInit, OnDestroy {
  @ContentChild(TemplateRef) templateVariable!: TemplateRef<any>;
  @Input() rows?: Oversikt[];
  @Input() row!: Oversikt;
  @Output() getResultEvent = new EventEmitter();

  private destroy$ = new Subject<void>();
  //private ref: DynamicDialogRef | undefined;
  private ref: DynamicDialogRef | null = null;
  protected antibiotikaDubblett = Anitbiotikadubblett
  protected currentFynd!: Fynd;
  protected type = NameType;
  protected nameStatus = NameStatus;
  protected okandNamnStatus = OkantNamnStatus;
  protected fyndView = 'full';
  protected viewToggleButtons: ButtonSetItem[] = [
    { text: 'Alla fynd', value: 'full', active: true },
    { text: 'Per fynd', value: 'panel' },
  ];

  constructor(
    private atgarListaService: AtgardsListaService,
    private messageService: MessageService,
    private dialogService: DialogService,
    private destroyRef: DestroyRef,
    private standardNamnService: StandardNamnService,
    @Inject('Clipboard') private clipboard: Clipboard
  ) {}
  ngOnInit(): void {
    this.atgarListaService.getDetails<AnalysResultatDTO>(this.row).pipe(takeUntilDestroyed(this.destroyRef)).subscribe(details => {
      this.row.details = details;
      this.setCurrentFynd(0);
    })
  }

  protected copy(value?: string) {
    this.clipboard.writeText(value as string);
    this.messageService.add({
      key: 'toast-copy',
      severity: 'success',
      detail: 'ProvID kopierat',
    });
  }

  protected onViewToggle(item: ButtonSetItem) {
    this.setCurrentFynd(0);
    this.fyndView = item.value;
  }

  private getOkantNamn(
    item: Oversikt,
    header: NameType,
    fynd?: Fynd,
    antibiotic?: Antibiotika
  ) {
    if (item.type === header) {
      if (item.type === NameType.Smittamne && fynd) {
        return fynd.smittamneName;
      }
      return item.name;
    }
    if (header === NameType.Analys) {
      return item.details.analysName;
    }
    if (header === NameType.Provmaterial) {
      return item.details.provdata.provmaterialName;
    }
    if (header === NameType.Smittamne) {
      return fynd?.smittamneName;
    }
    if (header === NameType.Antibiotika) {
      return antibiotic?.name;
    }
    return 'Invalid type';
  }

  private getOversiktRowByName(
    oversiktRow: Oversikt,
    header: NameType,
    fynd?: Fynd,
    antibiotic?: Antibiotika
  ) {
    const name = this.getOkantNamn(oversiktRow, header, fynd, antibiotic);
    const selectedRow = this.rows?.find(
      (row) => row['name'] === name && row['labCode'] === oversiktRow.labCode && row.type === header
    );

    if (!selectedRow)
      throw new Error('Could not find row with that name and labCode');

    selectedRow.selectedField = header;
    return selectedRow;
  }

  protected atgarda(
    oversiktRow: Oversikt,
    header: NameType,
    fynd?: Fynd,
    antibiotic?: Antibiotika
  ) {
    this.ref = this.dialogService.open(AtgardaEditDialogComponent, {
      header: `Åtgärda ${this.getAtgardsDialogTitle(header)}`,
      width: '',
      draggable: true,
      contentStyle: { overflow: 'visible' },
      baseZIndex: 10000,
      maximizable: false,
      data: {
        row: this.getOversiktRowByName(oversiktRow, header, fynd, antibiotic),
      },
    });

    this.ref?.onClose.pipe(takeUntil(this.destroy$)).subscribe((data: AtgardResult) => {
      if (data) {
        this.messageService.add({
          severity: data.result,
          detail: data.message,
        });

        this.atgarListaService.refetchAtgarder.next();
        this.standardNamnService.load(true);
      }
    });
  }

  protected showCommentHistorik(
    oversiktRow: Oversikt,
    header: NameType,
    fynd?: Fynd,
    antibiotic?: Antibiotika
  ) {
    this.ref = this.dialogService.open(HistorikDialogComponent, {
      header: `Historik`,
      // header: `Åtgärda ${this.getAtgardsDialogTitle(atgardItem.namntyp)}`,
      width: '',
      draggable: true,
      contentStyle: { overflow: 'auto' },
      baseZIndex: 10000,
      maximizable: false,
      data: {
        atgard: this.getOversiktRowByName(
          oversiktRow,
          header,
          fynd,
          antibiotic
        ),
      },
    });

    this.ref?.onClose.pipe(takeUntil(this.destroy$)).subscribe(() => {
      this.atgarListaService.refetchAtgarder.next();
    });
  }

  protected setCurrentFynd(index: number) {
    if (this.row && this.row.details)
      this.currentFynd = this.row.details.fyndList[index];
  }

  protected getStatus(item?: AtgardStatus): OkantNamnStatus | undefined {
    if (item?.ignored) {
      return OkantNamnStatus.Ignorerad;
    } else if (item?.nameStatus === NameStatus.Andrat) {
      return OkantNamnStatus.Synonymiserad;
    } else if (item?.nameStatus === NameStatus.Ej_Tolkningsbart) {
      return OkantNamnStatus.AtgardBehovs;
    } else if (item?.parked) {
      return OkantNamnStatus.Parkerad;
    } else {
      return undefined;
    }
  }

  private getAtgardsDialogTitle(namntyp: NameType | undefined) {
    switch (namntyp) {
      case NameType.Analys:
        return 'okänd analys';
      case NameType.Antibiotika:
        return 'okänd antibiotika';
      case NameType.Smittamne:
        return 'okänt smittämne';
      case NameType.Provmaterial:
        return 'okänt provmaterial';
      case NameType.NegativtFynd:
        return 'negativt fynd';
      default:
        return undefined;
    }
  }

  protected getToastStatusType(
    item?: AtgardStatus
  ): 'warning' | 'success' | 'danger' {
    const status = this.getStatus(item);
    switch (status) {
      case OkantNamnStatus.Parkerad:
        return 'warning';
      case OkantNamnStatus.AtgardBehovs:
        return 'danger';
      default:
        return 'success';
    }
  }

  protected getStatusText(item?: AtgardStatus): string {
    const status = this.getStatus(item);
    switch (status) {
      case OkantNamnStatus.Ignorerad:
        return 'Ignorerad';
      case OkantNamnStatus.Parkerad:
        return 'Parkerad';
      case OkantNamnStatus.Synonymiserad:
      case OkantNamnStatus.NyttStandardnamn:
        return 'Synonymiserad';
      case OkantNamnStatus.AtgardBehovs:
        return 'Åtgärd behövs';
      default:
        return '';
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
}
